import { useContext, useMemo } from "react";
import { Stack, IconButton } from "@fluentui/react";
import DOMPurify from "dompurify";

import styles from "./Answer.module.css";

import { AskResponse, getCitationFilePath } from "api";
import { parseAnswerToHtml } from "./AnswerParser";
import { AnswerIcon } from "./AnswerIcon";
import { SettingsContext } from "context";

interface Props {
  answer: AskResponse;
  isSelected?: boolean;
  onCitationClicked: (filePath: string) => void;
  onThoughtProcessClicked: () => void;
  onSupportingContentClicked: () => void;
  onFollowupQuestionClicked?: (question: string) => void;
  showFollowupQuestions?: boolean;
  endpoint: string;
  container?: string;
}

export const Answer = ({
  answer,
  isSelected,
  onCitationClicked,
  onThoughtProcessClicked,
  onSupportingContentClicked,
  onFollowupQuestionClicked,
  showFollowupQuestions,
  endpoint,
  container,
}: Props) => {
  const { currentDemo } = useContext(SettingsContext);

  const parsedAnswer = useMemo(
    () =>
      parseAnswerToHtml(
        answer.answer,
        endpoint,
        onCitationClicked,
        container,
        currentDemo.customerId === 1
      ),
    [answer]
  );

  const sanitizedAnswerHtml = DOMPurify.sanitize(parsedAnswer.answerHtml);

  return (
    <Stack
      className={`${styles.answerContainer} ${isSelected && styles.selected}`}
      verticalAlign="space-between"
    >
      <Stack.Item>
        <Stack horizontal horizontalAlign="end">
          {/* <AnswerIcon /> */}
          <div>
            <IconButton
              style={{ color: "black" }}
              iconProps={{ iconName: "Lightbulb" }}
              title="Show thought process"
              ariaLabel="Show thought process"
              onClick={() => onThoughtProcessClicked()}
              disabled={!answer?.thoughts}
            />
            <IconButton
              style={{ color: "black" }}
              iconProps={{ iconName: "ClipboardList" }}
              title="Show supporting content"
              ariaLabel="Show supporting content"
              onClick={() => onSupportingContentClicked()}
              disabled={!answer?.data_points?.length}
            />
          </div>
        </Stack>
      </Stack.Item>

      <Stack.Item grow>
        <div
          className={styles.answerText}
          dangerouslySetInnerHTML={{ __html: sanitizedAnswerHtml }}
        ></div>
      </Stack.Item>

      {!!answer?.data_points?.length && (
        <Stack.Item>
          <Stack horizontal wrap tokens={{ childrenGap: 5 }}>
            <span className={styles.citationLearnMore}>Citations:</span>
            {answer.data_points.map((x, i) => {
              const path = getCitationFilePath(
                x.split(":")[0],
                endpoint,
                container,
                currentDemo.customerId === 1
              );
              return (
                <a
                  key={i}
                  className={styles.citation}
                  title={x}
                  onClick={() => onCitationClicked(path)}
                >
                  {`${++i}. ${x.split(":")[0]}`}
                </a>
              );
            })}
          </Stack>
        </Stack.Item>
      )}

      {!!parsedAnswer?.followupQuestions.length &&
        showFollowupQuestions &&
        onFollowupQuestionClicked && (
          <Stack.Item>
            <Stack
              horizontal
              wrap
              className={`${
                !!parsedAnswer?.citations.length
                  ? styles.followupQuestionsList
                  : ""
              }`}
              tokens={{ childrenGap: 6 }}
            >
              <span className={styles.followupQuestionLearnMore}>
                Follow-up questions:
              </span>
              {parsedAnswer.followupQuestions.map((x, i) => {
                return (
                  <a
                    key={i}
                    className={styles.followupQuestion}
                    title={x}
                    onClick={() => onFollowupQuestionClicked(x)}
                  >
                    {`${x}`}
                  </a>
                );
              })}
            </Stack>
          </Stack.Item>
        )}
    </Stack>
  );
};
